import React from "react";
import { Card, CardHeader, CardBody, Spinner } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { threatCard, threatCardHeader, threatCardBody, threatIconChip } from "./cardStyles";

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const SEVERITY_COLORS = {
  Critical: '#dc2626',
  High: '#ea580c',
  Medium: '#eab308',
  Low: '#16a34a'
};

const AlertsBySeverity = ({ data, loading }) => {
  if (loading) {
    return (
      <Card style={{ 
        ...threatCard,
        height: '100%'
      }}>
        <CardHeader style={threatCardHeader}>
          <div className="d-flex align-items-center gap-3">
            <div style={threatIconChip()}>
              <i className="ri-alert-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <h5 className="card-title mb-0">Alerts by Severity</h5>
          </div>
        </CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px", ...threatCardBody }}>
          <Spinner style={{ color: ORANGE_COLORS.primary }} />
        </CardBody>
      </Card>
    );
  }

  const severityData = [
    { name: "Critical", value: data?.Critical || 0, color: SEVERITY_COLORS.Critical },
    { name: "High", value: data?.High || 0, color: SEVERITY_COLORS.High },
    { name: "Medium", value: data?.Medium || 0, color: SEVERITY_COLORS.Medium },
    { name: "Low", value: data?.Low || 0, color: SEVERITY_COLORS.Low }
  ];

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "Poppins, sans-serif"
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "60%",
        borderRadius: 8,
        distributed: true,
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val > 0 ? val : '';
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        colors: ['#858d98']
      }
    },
    xaxis: {
      categories: severityData.map(d => d.name),
      labels: {
        style: {
          colors: ['#858d98'],
          fontSize: '12px',
          fontWeight: 500
        }
      }
    },
    yaxis: {
      title: {
        text: "Alert Count",
        style: {
          color: '#858d98',
          fontSize: '12px',
          fontWeight: 500
        }
      },
      labels: {
        style: {
          colors: ['#858d98']
        }
      }
    },
    colors: severityData.map(d => d.color),
    legend: {
      show: false
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val) => `${val} alert${val !== 1 ? 's' : ''}`
      },
      style: {
        fontSize: '12px'
      }
    },
    grid: {
      borderColor: 'rgba(255, 107, 53, 0.1)',
      strokeDashArray: 4
    }
  };

  const series = [{
    name: "Alerts",
    data: severityData.map(d => d.value)
  }];

  const totalAlerts = severityData.reduce((sum, d) => sum + d.value, 0);

  return (
    <Card style={{ 
      ...threatCard,
      height: '100%'
    }}>
      <CardHeader style={threatCardHeader}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <div style={threatIconChip()}>
              <i className="ri-alert-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <h5 className="card-title mb-0">Alerts by Severity</h5>
          </div>
          {totalAlerts > 0 && (
            <span 
              className="badge" 
              style={{ 
                backgroundColor: 'rgba(255, 107, 53, 0.1)', 
                color: ORANGE_COLORS.primary,
                border: `1px solid ${ORANGE_COLORS.primary}`
              }}
            >
              {totalAlerts} total
            </span>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {totalAlerts === 0 ? (
          <div className="text-center py-5">
            <i className="ri-alert-line display-4 mb-3 d-block" style={{ color: ORANGE_COLORS.light }}></i>
            <p className="text-muted">No alerts to display</p>
          </div>
        ) : (
          <>
            <ReactApexChart options={options} series={series} type="bar" height={280} />
            
            {/* Severity Legend - Compact Single Row */}
<div className="mt-4 pt-3 border-top" style={{ borderColor: 'rgba(255, 107, 53, 0.2)' }}>
  <div className="d-flex justify-content-around align-items-center">
    {severityData.map((item, index) => (
      <div 
        key={index} 
        className="text-center"
      >
        <div 
          className="mx-auto mb-2"
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            backgroundColor: item.color
          }}
        ></div>
        <div className="small text-muted" style={{ fontSize: '11px' }}>
          {item.name}
        </div>
        <div 
          className="fw-bold" 
          style={{ 
            color: item.color,
            fontSize: '16px'
          }}
        >
          {item.value}
        </div>
      </div>
    ))}
  </div>
</div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default AlertsBySeverity;
